"use client";

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useRouter } from "next/navigation";
import type { Dictionary } from "@/i18n/dictionaries";
import type { Locale } from "@/i18n/config";
import { addDaysISO, MIN_BOOKING_ADVANCE_DAYS, SESSION_MINUTES } from "@/lib/availability";
import {
  formatPrice,
  type PaypalCurrency,
  PAYPAL_CURRENCIES,
  convertToPaypalCurrency,
} from "@/lib/format";
import { computeTotal, type PricingBreakdown } from "@/lib/pricing";
import {
  createPendingReservationAction,
  completeReservationPaymentAction,
  cancelPendingReservationAction,
} from "@/app/actions/reservation";
import type { BranchOption } from "@/lib/data/booking";
import { branchName, studioName } from "@/lib/data/types";
import { CountrySelect, countryDisplayName } from "@/components/country-select";
import { LanguageSelect, languageDisplayName } from "@/components/language-select";
import * as PortOne from "@portone/browser-sdk/v2";
import type { PortOnePublicConfig } from "@/lib/portone";

export interface WizardProduct {
  id: string;
  name: string;
  price: number | null;
  minGuests: number;
  maxGuests: number;
  baseGuests: number;
  extraGuestRatio: number;
  durationMinutes: number;
  brandId?: string | null;
  branchId?: string | null;
  studioId?: string | null;
  minAdvanceDays?: number;
  maxAdvanceDays?: number | null;
  validFrom?: string | null;
  validTo?: string | null;
}

interface SlotDto {
  startMinute: number;
  label: string;
  endMinute?: number;
  available: boolean;
}

interface BookingWizardProps {
  locale: Locale;
  dict: Dictionary;
  /** The program the visitor entered the wizard with (mutable via the program step) */
  initialProduct: WizardProduct;
  /** Programs available for selection in the program step */
  programs: WizardProduct[];
  branches: BranchOption[];
  /** Prefill from the hero widget (?studio=) — skips the branch step. */
  studioId?: string | null;
  /** Env-configured default selection — preselects without skipping the step. */
  defaultStudioId?: string | null;
  todayISO: string;
  minDateISO?: string;
  maxDateISO?: string;
  /** Prefill support — coming from the hero booking widget */
  initialDate?: string;
  initialStartMinute?: number;
  portOneConfig?: PortOnePublicConfig;
}

const MESSENGERS = ["KAKAO", "LINE", "WECHAT", "WHATSAPP", "TELEGRAM"] as const;
const GENDERS = ["MALE", "FEMALE", "OTHER"] as const;

function defaultCountryForLocale(locale: Locale): string {
  switch (locale) {
    case "ko": return "KR";
    case "ja": return "JP";
    case "zh": return "CN";
    case "zh-tw": return "TW";
    default: return "US";
  }
}

export function BookingWizard({
  locale,
  dict,
  initialProduct,
  programs,
  branches,
  studioId,
  defaultStudioId,
  todayISO,
  minDateISO,
  maxDateISO,
  initialDate,
  initialStartMinute,
  portOneConfig,
}: BookingWizardProps) {
  const router = useRouter();

  // The chosen program is mutable: the program step can switch it.
  const [product, setProduct] = useState<WizardProduct>(initialProduct);

  const getProductDateBounds = useCallback(
    (prod: WizardProduct) => {
      const minDays = prod.minAdvanceDays ?? MIN_BOOKING_ADVANCE_DAYS;
      let minD =
        minDateISO && prod.id === initialProduct.id
          ? minDateISO
          : addDaysISO(todayISO, minDays);
      if (prod.validFrom) {
        const validFromD = prod.validFrom.slice(0, 10);
        if (validFromD > minD) minD = validFromD;
      }

      const maxDays = prod.maxAdvanceDays ?? 90;
      let maxD =
        maxDateISO && prod.id === initialProduct.id
          ? maxDateISO
          : addDaysISO(todayISO, maxDays);
      if (prod.validTo) {
        const validToD = prod.validTo.slice(0, 10);
        if (validToD < maxD) maxD = validToD;
      }
      return { minDate: minD, maxDate: maxD, minDays, maxDays };
    },
    [todayISO, minDateISO, maxDateISO, initialProduct.id],
  );

  const { minDate, maxDate, minDays } = getProductDateBounds(product);
  const isDateValid =
    !!initialDate && initialDate >= minDate && initialDate <= maxDate;
  const hasPrefill =
    isDateValid && initialStartMinute !== undefined;

  // The branch a preselected studio belongs to (falls back to a single branch).
  const initialBranchId =
    (studioId && branches.find((b) => b.studios.some((s) => s.id === studioId))?.id) ??
    (defaultStudioId &&
      branches.find((b) => b.studios.some((s) => s.id === defaultStudioId))?.id) ??
    (branches.length === 1 ? branches[0].id : null);

  // ── Step state ───────────────────────────────────
  // 0: branch & studio, 1: program, 2: date & time, 3: guests, 4: details, 5: confirm.
  // A preselected studio (hero widget) skips steps 0 & 1. With a full hero
  // prefill (date + time) we also skip the date & time step and land on the
  // guests step, since the time is already chosen.
  const initialStep = studioId ? (hasPrefill ? 3 : 2) : 0;
  const [step, setStep] = useState(initialStep);

  // Step 0 — branch & studio
  const [wizardBranchId, setWizardBranchId] = useState<string | null>(
    initialBranchId,
  );
  const [wizardStudioId, setWizardStudioId] = useState<string | null>(
    studioId ?? defaultStudioId ?? null,
  );

  // Step 2 — date & time
  const [date, setDate] = useState(isDateValid ? initialDate! : "");
  const [slots, setSlots] = useState<SlotDto[] | null>(null); // null = loading
  const [slotError, setSlotError] = useState(false);
  const [startMinute, setStartMinute] = useState<number | null>(
    hasPrefill ? initialStartMinute! : null,
  );

  // Step 3 — guests
  const [guests, setGuests] = useState(product.minGuests);

  // Step 4 — details
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState(defaultCountryForLocale(locale));
  const [customerLanguage, setCustomerLanguage] = useState<string>(locale);
  const [gender, setGender] = useState("");
  const [messengerType, setMessengerType] = useState("");
  const [messengerId, setMessengerId] = useState("");
  const [songTitle, setSongTitle] = useState("");
  const [artistName, setArtistName] = useState("");
  const [songUrl, setSongUrl] = useState("");

  // Profile image (optional)
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const profileInputRef = useRef<HTMLInputElement>(null);

  // Step 5 — Payment & Payer
  const [selectedPayMethod, setSelectedPayMethod] = useState<"CARD" | "PAYPAL">("CARD");
  const [paypalCurrency, setPaypalCurrency] = useState<PaypalCurrency>("CURRENCY_USD");
  const [isPaypalModalOpen, setIsPaypalModalOpen] = useState(false);
  const [pendingPaypalReservationNo, setPendingPaypalReservationNo] = useState<string | null>(null);
  const [sameAsBooker, setSameAsBooker] = useState(true);
  const [payerName, setPayerName] = useState("");
  const [payerPhone, setPayerPhone] = useState("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Coupon
  const [couponInput, setCouponInput] = useState("");
  const [couponCode, setCouponCode] = useState<string | null>(null);
  const [couponName, setCouponName] = useState<string | null>(null);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [couponChecking, setCouponChecking] = useState(false);

  /* ── Live pricing — verified against the server on entry ── */
  const [livePricing, setLivePricing] = useState<{
    unitPrice: number | null;
    baseGuests: number;
    extraGuestRatio: number;
  } | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/pricing?product=${encodeURIComponent(product.id)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then(
        (data: {
          price?: { amount: number } | null;
          baseGuests?: number;
          extraGuestRatio?: number;
        } | null) => {
          if (cancelled || !data || data.price == null) return;
          setLivePricing({
            unitPrice: data.price.amount,
            baseGuests: data.baseGuests ?? product.baseGuests,
            extraGuestRatio: data.extraGuestRatio ?? 0,
          });
        },
      )
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [product.id, product.baseGuests]);

  // Server-verified values once loaded; page props as instant fallback
  const pricingMeta = livePricing ?? {
    unitPrice: product.price,
    baseGuests: product.baseGuests,
    extraGuestRatio: product.extraGuestRatio,
  };

  const total =
    pricingMeta.unitPrice !== null
      ? computeTotal({
          unitPrice: pricingMeta.unitPrice,
          guestsCount: guests,
          baseGuests: pricingMeta.baseGuests,
          extraGuestRatio: pricingMeta.extraGuestRatio,
          couponDiscount,
        })
      : (null as PricingBreakdown | null);

  const verifyCoupon = useCallback(
    async (code: string) => {
      const trimmed = code.trim();
      if (!trimmed || total == null) return;
      setCouponChecking(true);
      setCouponError(null);
      try {
        const params = new URLSearchParams({
          code: trimmed,
          amount: String(Math.round(total.subtotal)),
          ...(email ? { email } : {}),
          ...(product.brandId ? { brand: product.brandId } : {}),
          ...(product.branchId ? { branch: product.branchId } : {}),
        });
        const res = await fetch(`/api/coupon?${params.toString()}`);
        const data = (await res.json()) as
          | { ok: true; discountAmount: number; coupon: { code: string; name: string } }
          | { ok: false; reason: string };
        if (data.ok) {
          setCouponCode(data.coupon.code);
          setCouponName(data.coupon.name);
          setCouponDiscount(data.discountAmount);
        } else {
          setCouponCode(null);
          setCouponName(null);
          setCouponDiscount(0);
          setCouponError(
            dict.booking.couponError[
              data.reason as keyof typeof dict.booking.couponError
            ] ?? dict.booking.couponInvalid,
          );
        }
      } catch {
        setCouponCode(null);
        setCouponDiscount(0);
        setCouponError(dict.booking.couponInvalid);
      } finally {
        setCouponChecking(false);
      }
    },
    [total, email, product.brandId, product.branchId, dict],
  );

  const removeCoupon = useCallback(() => {
    setCouponInput("");
    setCouponCode(null);
    setCouponName(null);
    setCouponDiscount(0);
    setCouponError(null);
  }, []);

  /* ── Availability loading ────────────────────────
     All state updates happen after `await`, so effects stay clean. */
  const loadSlots = useCallback(
    async (
      targetDate: string,
      opts?: { autoSelect?: number },
    ) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(targetDate)) return;
      try {
        const res = await fetch(
          `/api/availability?product=${encodeURIComponent(product.id)}&date=${targetDate}`,
        );
        if (!res.ok) throw new Error("failed");
        const data = (await res.json()) as { slots?: SlotDto[] };
        const list = data.slots ?? [];
        setSlots(list);
        setSlotError(false);
        if (opts?.autoSelect !== undefined) {
          const match = list.find(
            (s) => s.startMinute === opts.autoSelect && s.available,
          );
          if (match) {
            setStartMinute(match.startMinute);
            // Fully prefilled — continue straight to the guests step.
            setStep(3);
            window.scrollTo({ top: 0, behavior: "smooth" });
          } else {
            setStartMinute(null);
            setError(null);
            // The prefilled slot is gone — let the visitor pick a time.
            setStep(2);
          }
        }
      } catch {
        setSlots([]);
        setSlotError(true);
      }
    },
    [product.id],
  );

  // Initial load (prefilled date comes from the hero widget)
  const bootKey = `${initialDate ?? ""}|${initialStartMinute ?? ""}|${product.id}`;
  const bootedRef = useRef("");
  useEffect(() => {
    if (bootedRef.current === bootKey || !initialDate) return;
    bootedRef.current = bootKey;
    void loadSlots(initialDate, {
      autoSelect: initialStartMinute,
    });
  }, [bootKey, initialDate, initialStartMinute, loadSlots]);

  const handleDateChange = (value: string) => {
    setDate(value);
    setStartMinute(null);
    setSlots(null);
    setSlotError(false);
    setError(null);
    if (/^\d{4}-\d{2}-\d{2}$/.test(value) && value >= minDate) {
      void loadSlots(value);
    }
  };

  const availableSlots = (slots ?? []).filter((s) => s.available);

  const handleProfileFile = async (file: File | undefined) => {
    if (!file) return;
    const isImage =
      file.type.toLowerCase().startsWith("image/") ||
      /\.(jpe?g|png|webp|avif|heic|heif|gif)$/i.test(file.name);
    if (!isImage) {
      setError(dict.booking.errorGeneric);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError(dict.booking.errorGeneric);
      return;
    }
    if (file.size === 0) {
      setError(dict.booking.errorGeneric);
      return;
    }
    setError(null);

    // Create instant local blob preview URL
    const localBlobUrl = URL.createObjectURL(file);
    setPreviewUrl(localBlobUrl);
    setUploadingImage(true);

    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch("/api/upload", { method: "POST", body: fd });
      const data = (await res.json()) as { success?: boolean; url?: string; error?: string };
      if (data.success && data.url) {
        setProfileImageUrl(data.url);
      } else {
        setError(data.error || dict.booking.errorGeneric);
        setPreviewUrl("");
      }
    } catch {
      setError(dict.booking.errorGeneric);
      setPreviewUrl("");
    } finally {
      setUploadingImage(false);
      if (profileInputRef.current) profileInputRef.current.value = "";
    }
  };

  const canProceed = () => {
    switch (step) {
      case 0:
        return wizardStudioId !== null;
      case 1:
        // A program is always selected (defaults to the entered one).
        return true;
      case 2:
        return date !== "" && startMinute !== null;
      case 3:
        return guests >= product.minGuests && guests <= product.maxGuests;
      case 4:
        return (
          name.trim() !== "" &&
          phone.trim() !== "" &&
          /.+@.+\..+/.test(email) &&
          songTitle.trim() !== "" &&
          artistName.trim() !== ""
        );
      default:
        return agreed;
    }
  };

  const handleNext = () => {
    if (!canProceed()) {
      setError(step === 4 ? dict.booking.errorFields : null);
      return;
    }
    setError(null);
    setStep((s) => Math.min(s + 1, dict.booking.steps.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleClosePaypalModal = async () => {
    if (pendingPaypalReservationNo) {
      await cancelPendingReservationAction(pendingPaypalReservationNo);
      setPendingPaypalReservationNo(null);
    }
    setIsPaypalModalOpen(false);
  };

  useEffect(() => {
    if (
      step !== 5 ||
      selectedPayMethod !== "PAYPAL" ||
      !isPaypalModalOpen ||
      !pendingPaypalReservationNo
    ) {
      return;
    }
    if (!portOneConfig?.storeId) return;
    const paypalChannelKey =
      portOneConfig.paypalChannelKey || portOneConfig.channelKey;
    if (!paypalChannelKey) return;
    const finalAmount = total ? Math.round(total.total) : 0;
    if (finalAmount <= 0) return;

    const paypalAmountInfo = convertToPaypalCurrency(finalAmount, paypalCurrency);

    const effectivePayerName =
      (sameAsBooker ? name : payerName).trim() || name.trim();
    const effectivePayerPhone =
      (sameAsBooker ? phone : payerPhone).trim() || phone.trim();

    const paymentId = `pay_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;

    let isCancelled = false;

    void PortOne.loadPaymentUI(
      {
        uiType: "PAYPAL_SPB",
        storeId: portOneConfig.storeId,
        channelKey: paypalChannelKey,
        paymentId,
        orderName: product.name,
        totalAmount: paypalAmountInfo.minorAmount,
        currency: paypalCurrency,
        customer: {
          fullName: effectivePayerName,
          phoneNumber: effectivePayerPhone,
          email: email.trim(),
        },
      },
      {
        onPaymentSuccess: async (response) => {
          if (isCancelled) return;
          setSubmitting(true);
          setError(null);
          try {
            const completeResult = await completeReservationPaymentAction({
              reservationNo: pendingPaypalReservationNo,
              payment: {
                paymentId,
                txId: response?.txId,
                payMethod: "PAYPAL",
                totalAmount: finalAmount,
                payerName: effectivePayerName,
                payerPhone: effectivePayerPhone,
              },
            });

            if (completeResult.ok) {
              router.push(`/${locale}/booking/complete/${pendingPaypalReservationNo}`);
            } else {
              setError(dict.booking.errorGeneric);
            }
          } catch {
            setError(dict.booking.errorGeneric);
          } finally {
            setSubmitting(false);
          }
        },
        onPaymentFail: async (err) => {
          if (isCancelled) return;
          console.error("[PortOne PayPal Error]", err);
          setError(err?.message || dict.booking.errorPaymentFailed);
          setSubmitting(false);
          if (pendingPaypalReservationNo) {
            await cancelPendingReservationAction(pendingPaypalReservationNo);
            setPendingPaypalReservationNo(null);
          }
          setIsPaypalModalOpen(false);
        },
      },
    );

    return () => {
      isCancelled = true;
      const container = document.getElementById("portone-ui-container");
      if (container) {
        container.innerHTML = "";
      }
    };
  }, [
    step,
    selectedPayMethod,
    isPaypalModalOpen,
    pendingPaypalReservationNo,
    paypalCurrency,
    portOneConfig,
    total,
    product.name,
    locale,
    name,
    phone,
    email,
    sameAsBooker,
    payerName,
    payerPhone,
    dict,
    router,
  ]);

  const handleSubmit = async () => {
    if (!agreed || startMinute === null || !date) {
      setError(dict.booking.errorAgree);
      return;
    }

    const finalAmount = total ? Math.round(total.total) : 0;
    const effectivePayerName = (sameAsBooker ? name : payerName).trim();
    const effectivePayerPhone = (sameAsBooker ? phone : payerPhone).trim();

    if (finalAmount > 0 && (!effectivePayerName || !effectivePayerPhone)) {
      setError(dict.booking.errorFields);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Step 1: Create PENDING reservation in DB before invoking payment (for both Card and PayPal)
      const pendingResult = await createPendingReservationAction({
        locale,
        productId: product.id,
        dateISO: date,
        startMinute,
        guestsCount: guests,
        couponCode: couponCode ?? undefined,
        studioId: wizardStudioId ?? undefined,
        customer: {
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim(),
          country,
          language: customerLanguage,
          gender: gender || undefined,
          messengerType: messengerType || undefined,
          messengerId: messengerId || undefined,
          profileImageUrl: profileImageUrl || undefined,
        },
        song: { songTitle, artistName, songUrl: songUrl || undefined },
      });

      if (!pendingResult.ok) {
        if (pendingResult.error === "SLOT_TAKEN") {
          setError(dict.booking.errorSlotTaken);
          setStep(2);
          void loadSlots(date);
        } else {
          setError(
            pendingResult.error === "INVALID"
              ? dict.booking.errorFields
              : dict.booking.errorGeneric,
          );
        }
        setSubmitting(false);
        return;
      }

      const reservationNo = pendingResult.reservationNo;

      // Free booking (e.g. 100% coupon) or fallback when PG is not configured
      if (finalAmount === 0 || !portOneConfig?.storeId || !portOneConfig?.channelKey) {
        const completeResult = await completeReservationPaymentAction({
          reservationNo,
        });

        if (completeResult.ok) {
          router.push(`/${locale}/booking/complete/${reservationNo}`);
        } else {
          setError(dict.booking.errorGeneric);
        }
        setSubmitting(false);
        return;
      }

      // If PayPal: Save pending reservationNo and open PayPal Checkout Modal
      if (selectedPayMethod === "PAYPAL") {
        setPendingPaypalReservationNo(reservationNo);
        setIsPaypalModalOpen(true);
        setSubmitting(false);
        return;
      }

      // Step 2: Trigger PortOne V2 PG (Credit Card only)
      const paymentId = `pay_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      const portOneLocale = locale === "ko" ? "KO_KR" : "EN_US";
      try {
        const response = await PortOne.requestPayment({
          storeId: portOneConfig.storeId,
          channelKey: portOneConfig.channelKey,
          paymentId,
          orderName: product.name,
          totalAmount: finalAmount,
          currency: "KRW",
          payMethod: "CARD",
          locale: portOneLocale,
          customer: {
            fullName: effectivePayerName,
            phoneNumber: effectivePayerPhone,
            email: email.trim(),
          },
        });

        if (response && response.code != null) {
          // Payment cancelled or failed: cancel pending reservation to free up the slot
          await cancelPendingReservationAction(reservationNo);
          setError(response.message || dict.booking.errorPaymentFailed);
          setSubmitting(false);
          return;
        }

        // Step 3: Complete payment & confirm reservation in DB
        const completeResult = await completeReservationPaymentAction({
          reservationNo,
          payment: {
            paymentId,
            txId: response?.txId,
            payMethod: "CARD",
            totalAmount: finalAmount,
            payerName: effectivePayerName,
            payerPhone: effectivePayerPhone,
          },
        });

        if (completeResult.ok) {
          router.push(`/${locale}/booking/complete/${reservationNo}`);
        } else {
          setError(dict.booking.errorGeneric);
        }
      } catch (pgErr) {
        await cancelPendingReservationAction(reservationNo);
        setError(
          pgErr instanceof Error ? pgErr.message : dict.booking.errorPaymentFailed,
        );
      }
    } catch {
      setError(dict.booking.errorGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto grid max-w-6xl gap-10 px-5 pt-32 pb-24 sm:px-8 lg:grid-cols-[1fr_360px] lg:gap-14">
      {/* ── Main column ──────────────────────────── */}
      <div>
        {/* Stepper */}
        <ol className="mb-12 flex items-center gap-2 sm:gap-3">
          {dict.booking.steps.map((label, i) => (
            <li key={label} className="flex flex-1 items-center gap-2 sm:gap-3">
              <button
                type="button"
                onClick={() => i < step && setStep(i)}
                disabled={i > step}
                aria-current={i === step ? "step" : undefined}
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border text-xs font-semibold transition-colors ${
                  i === step
                    ? "border-strong bg-strong text-paper"
                    : i < step
                      ? "cursor-pointer border-strong text-strong"
                      : "border-line-strong text-faint"
                }`}
              >
                {i < step ? "✓" : i + 1}
              </button>
              <span
                className={`hidden text-[0.6875rem] tracking-widest uppercase sm:block ${
                  i === step ? "text-strong" : "text-faint"
                }`}
              >
                {label}
              </span>
              {i < dict.booking.steps.length - 1 && (
                <span className="h-px flex-1 bg-line" aria-hidden />
              )}
            </li>
          ))}
        </ol>

        {/* Step 0 — Branch & Studio */}
        {step === 0 && (
          <section aria-labelledby="step-branch">
            <h2 id="step-branch" className="font-display mb-7 text-2xl text-strong sm:text-3xl">
              {dict.booking.selectBranch}
            </h2>

            {branches.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-display text-xl text-strong">
                  {dict.booking.branches}
                </h3>
                <ul className="space-y-2">
                  {branches.map((b) => {
                    const active = b.id === wizardBranchId;
                    return (
                      <li key={b.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setWizardBranchId(b.id);
                            setWizardStudioId(null);
                          }}
                          className={`flex w-full items-center justify-between rounded-lg border bg-paper px-5 py-4 text-left transition ${
                            active
                              ? "border-strong"
                              : "border-line hover:border-strong"
                          }`}
                        >
                          <span>
                            <span className="font-display text-lg text-strong">
                              {branchName(b, locale)}
                            </span>
                            {b.address && (
                              <span className="ml-2 text-xs text-faint">{b.address}</span>
                            )}
                          </span>
                          {active && <span className="text-sm text-accent-deep">✓</span>}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            <div className="mt-10 space-y-3">
              <h3 className="font-display text-xl text-strong">
                {dict.widget.selectStudio}
              </h3>
              {(() => {
                const activeBranch =
                  branches.find((b) => b.id === wizardBranchId) ??
                  branches[0] ??
                  null;
                if (!activeBranch) return null;
                return (
                  <ul className="space-y-2">
                    {activeBranch.studios.map((s) => {
                      const active = s.id === wizardStudioId;
                      return (
                        <li key={s.id}>
                          <button
                            type="button"
                            onClick={() => setWizardStudioId(s.id)}
                            className={`flex w-full items-center justify-between rounded-lg border bg-paper px-5 py-4 text-left transition ${
                              active
                                ? "border-strong"
                                : "border-line hover:border-strong"
                            }`}
                          >
                            <span>
                              <span className="font-display text-lg text-strong">
                                {studioName(s, locale)}
                              </span>
                              <span className="ml-2 text-xs text-faint">
                                {dict.booking.capacity.replace("{n}", String(s.capacity))}
                              </span>
                            </span>
                            {active && <span className="text-sm text-accent-deep">✓</span>}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                );
              })()}
            </div>
            {!wizardStudioId && (
              <p className="mt-4 text-xs text-red-400">{dict.widget.selectStudioRequired}</p>
            )}
          </section>
        )}

        {/* Step 1 — Program */}
        {step === 1 && (
          <section aria-labelledby="step-program">
            <h2
              id="step-program"
              className="font-display mb-7 text-2xl text-strong sm:text-3xl"
            >
              {dict.booking.selectProgram}
            </h2>
            {(() => {
              // List every program so the visitor can switch to a different one.
              const programList = programs.length ? programs : [product];
              return (
                <ul className="space-y-2">
                  {programList.map((p) => {
                    const active = p.id === product.id;
                    return (
                      <li key={p.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setProduct(p);
                            if (p.studioId) setWizardStudioId(p.studioId);
                            setGuests(
                              Math.min(
                                Math.max(p.baseGuests, p.minGuests),
                                p.maxGuests,
                              ),
                            );
                            const bounds = getProductDateBounds(p);
                            if (date && (date < bounds.minDate || date > bounds.maxDate)) {
                              setDate("");
                              setStartMinute(null);
                              setSlots(null);
                            }
                          }}
                          className={`flex w-full items-center justify-between gap-4 rounded-lg border bg-paper px-5 py-4 text-left transition ${
                            active
                              ? "border-strong"
                              : "border-line hover:border-strong"
                          }`}
                        >
                          <span>
                            <span className="font-display text-lg text-strong">
                              {p.name}
                            </span>
                            <span className="ml-2 text-xs text-faint">
                              {p.durationMinutes}
                              {dict.common.minutes}
                            </span>
                          </span>
                          <span className="text-right">
                            <span className="text-sm text-body">
                              {p.price !== null
                                ? formatPrice(p.price, locale)
                                : dict.common.inquiry}
                            </span>
                            {active && (
                              <span className="ml-3 text-sm text-accent-deep">✓</span>
                            )}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              );
            })()}
          </section>
        )}

        {/* Step 2 — Date & Time */}
        {step === 2 && (
          <section aria-labelledby="step-date">
            <h2 id="step-date" className="font-display mb-7 text-2xl text-strong sm:text-3xl">
              {dict.booking.selectDate}
            </h2>
            <div className="flex flex-wrap items-center gap-4">
              <input
                type="date"
                value={date}
                min={minDate}
                max={maxDate}
                onChange={(e) => handleDateChange(e.target.value)}
                className="field-input !w-auto min-w-[240px]"
                aria-label={dict.booking.selectDate}
              />
              <p className="text-xs text-faint">{weekdayLabel(dict, date)}</p>
            </div>
            <p className="mt-2 text-xs text-faint">
              {dict.common.minAhead.replace("{n}", String(minDays))}
            </p>

            {date && (
              <>
                <h3 className="font-display mt-10 mb-4 text-xl text-strong">
                  {dict.booking.selectTime}
                </h3>
                {slots === null ? (
                  <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-5" aria-busy>
                    {Array.from({ length: 10 }).map((_, i) => (
                      <div key={i} className="slot-card animate-pulse bg-mist" aria-hidden>
                        &nbsp;
                      </div>
                    ))}
                  </div>
                ) : slotError ? (
                  <p className="text-sm text-red-400">{dict.booking.errorGeneric}</p>
                ) : slots.length === 0 || availableSlots.length === 0 ? (
                  <p className="text-sm leading-relaxed text-muted">
                    {dict.booking.noSlots}
                  </p>
                ) : (
                  <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-5">
                    {slots.map((slot) => (
                      <button
                        key={slot.startMinute}
                        type="button"
                        className="slot-card"
                        disabled={!slot.available}
                        aria-pressed={startMinute === slot.startMinute}
                        onClick={() => setStartMinute(slot.startMinute)}
                      >
                        {slot.label}
                      </button>
                    ))}
                  </div>
                )}
                {slots !== null &&
                  !slotError &&
                  availableSlots.length > 0 &&
                  availableSlots.length <= 4 && (
                    <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-accent/40 bg-blush px-3.5 py-1.5 text-xs text-accent-deep">
                      <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden>
                        <circle cx="5" cy="5" r="4" fill="currentColor" />
                      </svg>
                      {dict.booking.soldOutSoon}
                    </p>
                  )}
              </>
            )}
          </section>
        )}

        {/* Step 3 — Guests */}
        {step === 3 && (
          <section aria-labelledby="step-guests">
            <h2 id="step-guests" className="font-display mb-2 text-2xl text-strong sm:text-3xl">
              {dict.booking.guestsTitle}
            </h2>
            <p className="text-xs text-faint">
              {dict.booking.guestsNote
                .replace("{min}", String(product.minGuests))
                .replace("{max}", String(product.maxGuests))}
            </p>
            <div className="mt-10 flex items-center justify-center gap-8 py-10">
              <CounterButton
                label="-"
                onClick={() => setGuests((g) => Math.max(product.minGuests, g - 1))}
                disabled={guests <= product.minGuests}
              />
              <div className="min-w-28 text-center">
                <p className="font-display text-6xl text-strong tabular-nums">{guests}</p>
                <p className="label-caps mt-2 text-[0.5625rem] text-faint">
                  {dict.booking.guestsTotal}
                </p>
              </div>
              <CounterButton
                label="+"
                onClick={() => setGuests((g) => Math.min(product.maxGuests, g + 1))}
                disabled={guests >= product.maxGuests}
              />
            </div>
          </section>
        )}

        {/* Step 4 — Details */}
        {step === 4 && (
          <section aria-labelledby="step-details">
            <h2 id="step-details" className="font-display mb-9 text-2xl text-strong sm:text-3xl">
              {dict.booking.detailsTitle}
            </h2>
            <div className="grid gap-6 sm:grid-cols-2">
              {/* Profile image */}
              <div className="sm:col-span-2">
                <span className="field-label">{dict.booking.profileImage}</span>
                <div className="flex items-center gap-5">
                  <button
                    type="button"
                    onClick={() => profileInputRef.current?.click()}
                    disabled={uploadingImage}
                    aria-label={dict.booking.profileImage}
                    className="group relative flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-full border border-dashed border-line-strong bg-canvas transition-colors hover:border-strong disabled:opacity-60"
                  >
                    {previewUrl || profileImageUrl ? (
                      // eslint-disable-next-line @next/next/no-img-element
                      <img
                        src={previewUrl || profileImageUrl}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" className="text-faint transition-colors group-hover:text-strong" aria-hidden>
                        <circle cx="12" cy="8.2" r="3.4" stroke="currentColor" strokeWidth="1.4" />
                        <path d="M4.8 19.4a7.6 7.6 0 0114.4 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                      </svg>
                    )}
                  </button>
                  <div>
                    <p className="text-sm text-body">
                      {uploadingImage
                        ? dict.booking.profileImageUploading
                        : previewUrl || profileImageUrl
                          ? dict.booking.profileImageSet
                          : dict.booking.profileImageHint}
                    </p>
                    <div className="mt-2 flex gap-4">
                      <button
                        type="button"
                        onClick={() => profileInputRef.current?.click()}
                        disabled={uploadingImage}
                        className="text-xs font-medium tracking-[0.14em] text-accent-deep uppercase transition-colors hover:text-strong disabled:opacity-50"
                      >
                        {previewUrl || profileImageUrl ? dict.booking.profileImageChange : dict.booking.profileImageSelect}
                      </button>
                      {(previewUrl || profileImageUrl) && (
                        <button
                          type="button"
                          onClick={() => {
                            setProfileImageUrl("");
                            setPreviewUrl("");
                          }}
                          className="text-xs font-medium tracking-[0.14em] text-muted uppercase transition-colors hover:text-red-500"
                        >
                          {dict.booking.profileImageRemove}
                        </button>
                      )}
                    </div>
                  </div>
                    <input
                     ref={profileInputRef}
                     type="file"
                     accept="image/*"
                     className="hidden"
                     onChange={(e) => void handleProfileFile(e.target.files?.[0])}
                   />
                </div>
              </div>
              <div>
                <label htmlFor="bw-name" className="field-label">{dict.booking.name} *</label>
                <input id="bw-name" className="field-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={dict.booking.namePlaceholder} autoComplete="name" required />
              </div>
              <div>
                <label htmlFor="bw-phone" className="field-label">{dict.booking.phone} *</label>
                <input id="bw-phone" type="tel" className="field-input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={dict.booking.phonePlaceholder} autoComplete="tel" required />
                <p className="mt-1.5 text-xs text-faint">{dict.booking.phoneNote}</p>
              </div>
              <div>
                <label htmlFor="bw-email" className="field-label">{dict.booking.email} *</label>
                <input id="bw-email" type="email" className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={dict.booking.emailPlaceholder} autoComplete="email" required />
                <p className="mt-1.5 text-xs text-faint">{dict.booking.emailNote}</p>
              </div>
              <div>
                <label htmlFor="bw-country" className="field-label">{dict.booking.country}</label>
                <CountrySelect
                  id="bw-country"
                  value={country}
                  onChange={setCountry}
                  locale={locale}
                  placeholder={dict.booking.countrySearchPlaceholder ?? dict.booking.country}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="bw-language" className="field-label">{dict.booking.language}</label>
                <LanguageSelect
                  id="bw-language"
                  value={customerLanguage}
                  onChange={setCustomerLanguage}
                  locale={locale}
                  placeholder={dict.booking.languageSearchPlaceholder ?? dict.booking.language}
                />
              </div>
              <fieldset>
                <legend className="field-label">{dict.booking.gender}</legend>
                <div className="flex gap-2">
                  {GENDERS.map((g) => (
                    <button key={g} type="button" className="slot-card flex-1" aria-pressed={gender === g} onClick={() => setGender(gender === g ? "" : g)}>
                      {dict.genders[g]}
                    </button>
                  ))}
                </div>
              </fieldset>
              <fieldset>
                <legend className="field-label">{dict.booking.contactMessenger}</legend>
                <div className="grid grid-cols-2 gap-2">
                  {MESSENGERS.map((m) => (
                    <button key={m} type="button" className="slot-card" aria-pressed={messengerType === m} onClick={() => setMessengerType(messengerType === m ? "" : m)}>
                      {dict.messengers[m]}
                    </button>
                  ))}
                </div>
              </fieldset>
              {messengerType && (
                <div className="sm:col-span-2">
                  <label htmlFor="bw-mid" className="field-label">{dict.booking.messengerId}</label>
                  <input id="bw-mid" className="field-input" value={messengerId} onChange={(e) => setMessengerId(e.target.value)} autoComplete="off" />
                </div>
              )}
            </div>

            <div className="rule-h my-10" />

            <h3 className="font-display mb-6 text-xl text-strong">{dict.booking.songSection}</h3>
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="bw-song" className="field-label">{dict.booking.songTitle} *</label>
                <input id="bw-song" className="field-input" value={songTitle} onChange={(e) => setSongTitle(e.target.value)} placeholder={dict.booking.songPlaceholder} required />
              </div>
              <div>
                <label htmlFor="bw-artist" className="field-label">{dict.booking.artist} *</label>
                <input id="bw-artist" className="field-input" value={artistName} onChange={(e) => setArtistName(e.target.value)} placeholder={dict.booking.artistPlaceholder} required />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="bw-url" className="field-label">{dict.booking.referenceUrl}</label>
                <input id="bw-url" type="url" className="field-input" value={songUrl} onChange={(e) => setSongUrl(e.target.value)} placeholder={dict.booking.urlPlaceholder} />
                <p className="mt-1.5 text-xs text-faint">{dict.booking.keyNote}</p>
              </div>
            </div>
          </section>
        )}

        {/* Step 5 — Review */}
        {step === 5 && (
          <section aria-labelledby="step-review">
            <h2 id="step-review" className="font-display mb-9 text-2xl text-strong sm:text-3xl">
              {dict.booking.reviewTitle}
            </h2>
            <dl className="divide-y divide-line border border-line">
              <ReviewRow label={dict.booking.reviewProgram}>
                {product.name}
              </ReviewRow>
              <ReviewRow label={dict.booking.reviewStudio}>
                {(() => {
                  const studio = branches
                    .flatMap((b) => b.studios)
                    .find((s) => s.id === wizardStudioId);
                  const branch =
                    branches.find((b) => b.id === wizardBranchId) ??
                    branches.find((b) => b.studios.some((s) => s.id === wizardStudioId));
                  if (!studio) return "—";
                  const bName = branchName(branch, locale);
                  const sName = studioName(studio, locale);
                  return bName ? `${bName} · ${sName}` : sName;
                })()}
              </ReviewRow>
              <ReviewRow label={dict.booking.reviewDateTime}>
                {formatSeoulDate(date, locale)}
                {startMinute !== null &&
                  ` · ${minutesToLabel(startMinute)} – ${minutesToLabel(startMinute + SESSION_MINUTES)}`}
              </ReviewRow>
              <ReviewRow label={dict.booking.reviewGuests}>
                {guests}{dict.common.guests}
              </ReviewRow>
              <ReviewRow label={dict.booking.reviewContact}>
                <span className="flex items-center gap-4">
                  {(previewUrl || profileImageUrl) && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={previewUrl || profileImageUrl}
                      alt=""
                      className="h-11 w-11 shrink-0 rounded-full border border-line object-cover"
                    />
                  )}
                  <span>
                    <span className="font-medium text-strong">{name}</span>
                    <br />
                    <span className="text-faint">{phone} · {email}</span>
                    <br />
                    <span className="text-xs text-faint">
                      {countryDisplayName(country, locale)} ({country}) ·{" "}
                      {languageDisplayName(customerLanguage, locale)}
                    </span>
                  </span>
                </span>
              </ReviewRow>
              <ReviewRow label={dict.booking.reviewSong}>
                “{songTitle}” — {artistName}
              </ReviewRow>

              {/* Coupon */}
              <div className="px-1 py-4">
                <span className="label-caps text-[0.625rem] text-faint">
                  {dict.booking.couponLabel}
                </span>
                {couponCode ? (
                  <div className="mt-2 flex items-center justify-between gap-3 rounded-md border border-line bg-canvas px-3 py-2">
                    <span className="text-sm text-body">
                      <span className="font-medium text-strong">{couponCode}</span>
                      {couponName ? (
                        <span className="ml-2 text-faint">{couponName}</span>
                      ) : null}
                      <span className="ml-2 font-medium text-accent-deep">
                        −{formatPrice(couponDiscount, locale)}
                      </span>
                    </span>
                    <button
                      type="button"
                      onClick={removeCoupon}
                      className="text-xs text-faint underline-offset-2 hover:text-strong hover:underline"
                    >
                      {dict.booking.couponRemove}
                    </button>
                  </div>
                ) : (
                  <div className="mt-2 flex items-end gap-2">
                    <input
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          void verifyCoupon(couponInput);
                        }
                      }}
                      placeholder={dict.booking.couponPlaceholder}
                      className="flex-1 rounded-md border border-line bg-paper px-3 py-2 text-sm text-strong outline-none transition placeholder:text-faint focus:border-strong"
                    />
                    <button
                      type="button"
                      onClick={() => void verifyCoupon(couponInput)}
                      disabled={couponChecking || !couponInput.trim()}
                      className="btn-ghost shrink-0 disabled:opacity-40"
                    >
                      {couponChecking ? dict.booking.processing : dict.booking.couponApply}
                    </button>
                  </div>
                )}
                {couponError ? (
                  <p className="mt-2 text-xs text-red-600">{couponError}</p>
                ) : null}
              </div>

              <ReviewRow label={dict.booking.reviewPrice} emphasis>
                {product.price !== null && total ? (
                  <>
                    {couponDiscount > 0 ? (
                      <span className="flex items-baseline gap-2">
                        <span className="text-base text-faint line-through">
                          {formatPrice(total.subtotal, locale)}
                        </span>
                        <span>{formatPrice(total.total, locale)}</span>
                      </span>
                    ) : (
                      formatPrice(total.total, locale)
                    )}
                    <span className="mt-2 block text-xs font-normal text-faint">
                      {dict.booking.baseIncluded.replace(
                        "{n}",
                        String(product.baseGuests),
                      )}{" "}
                      {formatPrice(total.basePrice, locale)}
                      {total.extraGuests > 0 && (
                        <>
                          {" + "}
                          {dict.booking.extraGuestsShort}
                          {total.extraGuests} ×{" "}
                          {formatPrice(total.extraCharge / total.extraGuests, locale)} (
                          {total.extraGuestRatio}%)
                        </>
                      )}
                      {couponDiscount > 0 && (
                        <>
                          <br />
                          {dict.booking.couponDiscountLabel}{" "}
                          <span className="text-accent-deep">
                            −{formatPrice(couponDiscount, locale)}
                          </span>
                        </>
                      )}
                    </span>
                  </>
                ) : (
                  dict.common.inquiry
                )}
              </ReviewRow>
            </dl>

            {/* Payment & Payer Info */}
            {total && total.total > 0 && (
              <div className="mt-8 space-y-6 rounded-lg border border-line bg-mist/60 p-5 sm:p-6">
                {/* Method selection */}
                <div>
                  <div className="flex items-center justify-between">
                    <h3 className="font-display text-base font-medium text-strong">
                      {dict.booking.payMethod}
                    </h3>
                    <span className="text-[0.6875rem] font-medium tracking-wider text-faint uppercase">
                      PortOne · V2
                    </span>
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {/* Card (KG Inicis) */}
                    <button
                      type="button"
                      onClick={() => setSelectedPayMethod("CARD")}
                      className={`flex items-center gap-3 rounded-md border p-3.5 text-left transition-all ${
                        selectedPayMethod === "CARD"
                          ? "border-strong bg-paper shadow-xs"
                          : "border-line bg-canvas hover:border-line-strong"
                      }`}
                    >
                      <div
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                          selectedPayMethod === "CARD"
                            ? "bg-strong text-paper"
                            : "border border-line bg-paper text-faint"
                        }`}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden
                        >
                          <rect width="20" height="14" x="2" y="5" rx="2" />
                          <line x1="2" x2="22" y1="10" y2="10" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-strong">
                          {dict.booking.payMethodCard}
                        </p>
                        <p className="text-xs text-faint">KG Inicis</p>
                      </div>
                    </button>

                    {/* PayPal */}
                    <button
                      type="button"
                      onClick={() => setSelectedPayMethod("PAYPAL")}
                      className={`flex items-center gap-3 rounded-md border p-3.5 text-left transition-all ${
                        selectedPayMethod === "PAYPAL"
                          ? "border-[#003087] bg-paper shadow-xs"
                          : "border-line bg-canvas hover:border-line-strong"
                      }`}
                    >
                      <div
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                          selectedPayMethod === "PAYPAL"
                            ? "bg-[#003087] text-white"
                            : "border border-line bg-paper text-faint"
                        }`}
                      >
                        <span className="text-xs font-black italic">P</span>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-strong">
                          {dict.booking.payMethodPaypal}
                        </p>
                        <p className="text-xs text-faint">PayPal · SPB</p>
                      </div>
                    </button>
                  </div>
                </div>

                {/* PayPal info card */}
                {selectedPayMethod === "PAYPAL" && (
                  <div className="rounded-md border border-[#003087]/20 bg-[#003087]/5 p-4 sm:p-5">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#003087] text-white">
                        <span className="text-xs font-black italic">P</span>
                      </div>
                      <p className="text-sm font-semibold text-[#003087]">
                        PayPal Smart Payment Buttons (SPB)
                      </p>
                    </div>
                    <p className="mt-2.5 text-xs leading-relaxed text-body">
                      {dict.booking.paypalCardNotice}
                    </p>
                  </div>
                )}

                {/* Payer information */}
                <div className="border-t border-line pt-5">
                  <div className="flex items-center justify-between">
                    <h4 className="font-display text-sm font-medium text-strong">
                      {dict.booking.payerInfo}
                    </h4>
                    <label className="flex cursor-pointer items-center gap-2 text-xs text-body">
                      <input
                        type="checkbox"
                        checked={sameAsBooker}
                        onChange={(e) => setSameAsBooker(e.target.checked)}
                        className="h-3.5 w-3.5 accent-[#23211d]"
                      />
                      {dict.booking.sameAsBooker}
                    </label>
                  </div>

                  {!sameAsBooker && (
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="bw-payer-name" className="field-label">
                          {dict.booking.payerName} *
                        </label>
                        <input
                          id="bw-payer-name"
                          className="field-input bg-paper"
                          value={payerName}
                          onChange={(e) => setPayerName(e.target.value)}
                          placeholder={dict.booking.payerNamePlaceholder}
                          required
                        />
                      </div>
                      <div>
                        <label htmlFor="bw-payer-phone" className="field-label">
                          {dict.booking.payerPhone} *
                        </label>
                        <input
                          id="bw-payer-phone"
                          type="tel"
                          className="field-input bg-paper"
                          value={payerPhone}
                          onChange={(e) => setPayerPhone(e.target.value)}
                          placeholder={dict.booking.payerPhonePlaceholder}
                          required
                        />
                      </div>
                    </div>
                  )}
                </div>

                <p className="text-xs leading-relaxed text-faint">
                  {selectedPayMethod === "PAYPAL"
                    ? dict.booking.paypalNotice
                    : dict.booking.payNotice}
                </p>
              </div>
            )}

            <label className="mt-7 flex cursor-pointer items-start gap-3 text-sm text-body">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[#23211d]"
              />
              {dict.booking.agreeTerms}
            </label>
          </section>
        )}

        {error && (
          <p role="alert" className="mt-8 border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {error}
          </p>
        )}

        {/* Nav buttons */}
        <div className="mt-12 flex items-center justify-between gap-4">
          <button
            type="button"
            className="btn-ghost"
            onClick={() =>
              hasPrefill && step === 0 ? router.back() : setStep((s) => Math.max(0, s - 1))
            }
            disabled={submitting}
          >
            {dict.booking.prev}
          </button>
          {step < dict.booking.steps.length - 1 ? (
            <button type="button" className="btn-dark" onClick={handleNext}>
              {dict.booking.next}
            </button>
          ) : (
            <button
              type="button"
              className="btn-dark"
              onClick={handleSubmit}
              disabled={submitting || !agreed}
            >
              {submitting
                ? dict.booking.processing
                : total && total.total > 0
                  ? selectedPayMethod === "PAYPAL"
                    ? `PayPal ${dict.booking.payButton}`
                    : dict.booking.payButton
                  : dict.booking.confirm}
            </button>
          )}
        </div>
      </div>

      {/* ── Sticky summary ───────────────────────── */}
      <aside className="h-fit lg:sticky lg:top-28">
        <div className="rounded-lg border border-line bg-paper p-7 shadow-[0_28px_70px_-38px_rgba(35,33,29,0.35)]">
          <h2 className="font-display text-lg text-strong">
            {dict.booking.summarySticky}
          </h2>
          <div className="rule-h my-5" />
          <dl className="space-y-4 text-sm">
            <SummaryRow label={dict.booking.reviewProgram}>
              {product.name}
            </SummaryRow>
            <SummaryRow label={dict.booking.reviewStudio}>
              {(() => {
                const studio = branches
                  .flatMap((b) => b.studios)
                  .find((s) => s.id === wizardStudioId);
                const branch =
                  branches.find((b) => b.id === wizardBranchId) ??
                  branches.find((b) => b.studios.some((s) => s.id === wizardStudioId));
                if (!studio) return "—";
                return (
                  <span className="inline-flex flex-wrap items-center gap-x-1.5 gap-y-1">
                    <span className="text-faint">{dict.booking.branchLabel}</span>
                    <span className="text-strong">{branchName(branch, locale) || "—"}</span>
                    <span className="text-faint">·</span>
                    <span className="text-faint">{dict.widget.selectStudio}</span>
                    <span className="text-strong">{studioName(studio, locale)}</span>
                  </span>
                );
              })()}
            </SummaryRow>
            <SummaryRow label={dict.booking.reviewDateTime}>
              {date ? formatSeoulDate(date, locale) : "—"}
              {startMinute !== null && (
                <>
                  <br />
                  <span className="tabular-nums">
                    {minutesToLabel(startMinute)} –{" "}
                    {minutesToLabel(startMinute + SESSION_MINUTES)}
                  </span>
                </>
              )}
            </SummaryRow>
            <SummaryRow label={dict.booking.reviewGuests}>
              {guests}{dict.common.guests}
            </SummaryRow>
          </dl>
          <div className="rule-h my-5" />
          <div className="flex items-end justify-between">
            <span className="label-caps text-[0.5625rem] text-faint">{dict.booking.reviewPrice}</span>
            <span className="font-display text-2xl text-strong tabular-nums">
              {product.price !== null && total ? (
                couponDiscount > 0 ? (
                  <span className="flex flex-col items-end leading-tight">
                    <span className="text-sm text-faint line-through">
                      {formatPrice(total.subtotal, locale)}
                    </span>
                    {formatPrice(total.total, locale)}
                  </span>
                ) : (
                  formatPrice(total.total, locale)
                )
              ) : (
                dict.common.inquiry
              )}
            </span>
          </div>
          {total && total.extraGuests > 0 && (
            <p className="mt-3 text-right text-xs text-faint">
              {dict.booking.extraGuestsShort}
              {total.extraGuests} ×{" "}
              {formatPrice(total.extraCharge / total.extraGuests, locale)} (
              +{total.extraGuestRatio}%) ={" "}
              {formatPrice(total.extraCharge, locale)}
            </p>
          )}
          {couponDiscount > 0 && (
            <p className="mt-3 text-right text-xs text-accent-deep">
              {dict.booking.couponDiscountLabel}{" "}
              −{formatPrice(couponDiscount, locale)}
            </p>
          )}
        </div>
      </aside>

      {/* ── PayPal Checkout Modal ────────────────────── */}
      {isPaypalModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200"
          role="dialog"
          aria-modal="true"
          aria-labelledby="paypal-modal-title"
        >
          <div className="relative flex flex-col w-full max-w-lg max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-3.5rem)] rounded-xl border border-line bg-paper shadow-2xl overflow-hidden">
            {/* Modal Header */}
            <div className="shrink-0 flex items-center justify-between border-b border-line px-6 py-4 sm:px-7">
              <div className="flex items-center gap-2.5">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#003087] text-white shadow-xs">
                  <span className="text-xs font-black italic">P</span>
                </div>
                <h3 id="paypal-modal-title" className="font-display text-lg font-medium text-strong">
                  {dict.booking.paypalModalTitle}
                </h3>
              </div>
              <button
                type="button"
                onClick={handleClosePaypalModal}
                className="rounded-md p-1.5 text-faint hover:bg-mist hover:text-strong transition-colors"
                aria-label={dict.booking.paypalModalClose}
                disabled={submitting}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Modal Scrollable Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5 sm:px-7 space-y-5">
              {/* Program & Total Amount summary */}
              <div className="rounded-lg bg-mist/60 p-3.5 flex items-center justify-between text-sm">
                <span className="text-body font-medium">{product.name}</span>
                <span className="font-display font-semibold text-strong">
                  {total ? formatPrice(total.total, locale) : ""}
                </span>
              </div>

              {/* Currency Selector */}
              <div>
                <label className="field-label text-xs font-medium text-strong">
                  {dict.booking.paypalCurrencySelect}
                </label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {(Object.keys(PAYPAL_CURRENCIES) as PaypalCurrency[]).map((cur) => {
                    const info = PAYPAL_CURRENCIES[cur];
                    const converted = convertToPaypalCurrency(
                      total ? Math.round(total.total) : 0,
                      cur,
                    );
                    const isSelected = paypalCurrency === cur;
                    return (
                      <button
                        key={cur}
                        type="button"
                        onClick={() => setPaypalCurrency(cur)}
                        disabled={submitting}
                        className={`flex flex-col items-center justify-center rounded-md border p-2.5 transition-all ${
                          isSelected
                            ? "border-[#003087] bg-[#003087]/5 font-semibold text-[#003087] shadow-xs"
                            : "border-line bg-canvas text-body hover:border-line-strong"
                        }`}
                      >
                        <span className="text-xs font-bold">{info.label}</span>
                        <span className="mt-0.5 text-xs font-medium">{converted.formatted}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* PayPal Notice & SPB container */}
              <div className="rounded-lg border border-line bg-canvas p-4 text-center">
                <p className="text-xs text-body mb-3">
                  {dict.booking.paypalNotice}
                </p>
                <div
                  id="portone-ui-container"
                  className="portone-ui-container flex min-h-[48px] items-center justify-center w-full my-2"
                />
                <p className="mt-2 text-[0.6875rem] text-faint">
                  {dict.booking.paypalButtonHint}
                </p>
              </div>
            </div>

            {/* Modal Footer / Close button */}
            <div className="shrink-0 flex justify-end border-t border-line px-6 py-3.5 sm:px-7 bg-mist/30">
              <button
                type="button"
                onClick={handleClosePaypalModal}
                className="btn-ghost text-xs px-4 py-2"
                disabled={submitting}
              >
                {dict.booking.paypalModalClose}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── helpers & subcomponents ─────────────────────── */

function minutesToLabel(minute: number): string {
  const h = Math.floor(minute / 60);
  const m = minute % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function localeTag(locale: Locale): string {
  switch (locale) {
    case "ko": return "ko-KR";
    case "ja": return "ja-JP";
    case "zh": return "zh-CN";
    case "zh-tw": return "zh-TW";
    default: return "en-US";
  }
}

function formatSeoulDate(dateISO: string, locale: Locale): string {
  if (!dateISO) return "";
  const [y, m, d] = dateISO.split("-").map(Number);
  return new Intl.DateTimeFormat(localeTag(locale), {
    timeZone: "UTC",
    year: "numeric",
    month: "long",
    day: "numeric",
    weekday: "short",
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

function weekdayLabel(dict: Dictionary, dateISO: string): string {
  if (!dateISO) return "";
  const dow = new Date(`${dateISO}T00:00:00Z`).getUTCDay();
  const weekend = dow === 0 || dow === 6;
  const label = new Intl.DateTimeFormat(localeTag("en"), {
    timeZone: "UTC",
    weekday: "long",
  })
    .format(new Date(`${dateISO}T00:00:00Z`))
    .toUpperCase();
  return `${weekend ? "★ " : ""}${label}`;
}

function CounterButton({ label, onClick, disabled }: { label: "-" | "+"; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label === "+" ? "increase" : "decrease"}
      className="flex h-14 w-14 items-center justify-center rounded-full border border-line-strong text-2xl text-strong transition-colors hover:border-strong hover:bg-canvas disabled:opacity-30 disabled:hover:border-line-strong disabled:hover:bg-transparent"
    >
      {label}
    </button>
  );
}

function ReviewRow({ label, children, emphasis }: { label: string; children: ReactNode; emphasis?: boolean }) {
  return (
    <div className={`px-6 py-5 ${emphasis ? "bg-blush" : ""}`}>
      <dt className="label-caps text-[0.5625rem] text-faint">{label}</dt>
      <dd className={`mt-2 leading-relaxed ${emphasis ? "font-display text-2xl text-strong tabular-nums" : "text-strong"}`}>
        {children}
      </dd>
    </div>
  );
}

function SummaryRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="label-caps text-[0.5625rem] text-faint">{label}</dt>
      <dd className="mt-1.5 leading-relaxed text-strong">{children}</dd>
    </div>
  );
}
